import { useEffect, type ReactElement } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FullPageLoader } from "@/components/loading";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { useAuth } from "@/hooks/use-auth";

/**
 * The address every staff notification about a ticket points at.
 *
 * The server builds those links without knowing who will read them — one escalation raises
 * notifications for the assigned team leader and for every administrator at once — so it emits a
 * single role-agnostic `/tickets/{id}` and leaves the routing to the client. This route is that
 * resolution step: it forwards to whichever supervisor surface the reader is entitled to, carrying
 * the ticket id so the destination opens it directly.
 *
 * Without it the most urgent notifications in the product — SLA breached, ticket escalated — would
 * land on a page that does not exist. An admin is checked first because a hybrid admin who also
 * leads a group should land on the board that shows every ticket, not the subset they lead.
 */
export const Route = createFileRoute("/_authenticated/tickets_/$ticketId")({
  component: TicketDeepLinkPage,
});

function TicketDeepLinkPage(): ReactElement {
  const { ticketId } = Route.useParams();
  const { isAdmin, isSuperAdmin, isTeamLeader } = useAuth();
  const navigate = useNavigate();

  const isAdministrator = isAdmin || isSuperAdmin;
  const canSupervise = isAdministrator || isTeamLeader;

  useEffect(() => {
    if (!canSupervise) return;

    navigate({
      to: isAdministrator ? "/admin/tickets" : "/team-leader/tickets",
      search: { ticketId },
      replace: true,
    });
  }, [canSupervise, isAdministrator, navigate, ticketId]);

  if (!canSupervise) {
    return (
      <AccessDenied message="Support tickets are managed by team leaders and administrators. If this ticket is yours, open it from the Support page." />
    );
  }

  return <FullPageLoader />;
}
